var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Defines aggregations settings for the current context.
 * Group, aggregate columns, grand totals, etc.
 */
var AggregationSettings = /** @class */ (function () {
    function AggregationSettings(colStore) {
        this.colStore = colStore;
        this.aggregates = [];
        this.groups = [];
        this.useGrandTotals = false;
        this.useRecordCount = false;
        this._caseSensitiveGroups = false;
        this.COUNT_FIELD_NAME = 'GRPRECCNT';
    }
    Object.defineProperty(AggregationSettings.prototype, "caseSensitiveGroups", {
        get: function () {
            return this._caseSensitiveGroups;
        },
        set: function (value) {
            this._caseSensitiveGroups = value;
            this.updateCompareProc();
        },
        enumerable: true,
        configurable: true
    });
    AggregationSettings.prototype.updateCompareProc = function () {
        this.compareValues = this._caseSensitiveGroups
            ? this.strictCompare
            : this.caseInsensitiveCompare;
    };
    AggregationSettings.prototype.addGroup = function (settings) {
        var cols = settings.columns || this.colStore.getColumnIds(settings.from, settings.to);
        if (!this.colStore.validateColumns(cols))
            throw "Invalid columns: " + cols;
        if (this.hasColumnsInUse(cols))
            throw "Can't add same columns to different groups/aggregates";
        this.groups.push(__assign({ columns: cols }, settings));
        return this;
    };
    AggregationSettings.prototype.addAggregateColumn = function (colIndexOrId, funcId) {
        var colId = typeof colIndexOrId == 'string'
            ? colIndexOrId
            : this.colStore.getColumnIds(colIndexOrId, colIndexOrId)[0];
        if (this.hasColumnsInUse([colId]) || !this.colStore.validateAggregate(colId, funcId))
            throw 'Invalid aggregation function for the column: ' + colId;
        this.aggregates.push({ colId: colId, funcId: funcId });
        return this;
    };
    AggregationSettings.prototype.addGrandTotals = function () {
        this.useGrandTotals = true;
        return this;
    };
    AggregationSettings.prototype.addCounts = function () {
        this.useRecordCount = true;
        return this;
    };
    AggregationSettings.prototype.getGroups = function () {
        var _this = this;
        var cols = [];
        var mappedGrops = this.groups.map(function (grp) {
            cols = cols.concat(grp.columns);
            return __assign(__assign({}, grp), { columns: Array.from(cols), aggregates: Array.from(_this.aggregates) });
        });
        return mappedGrops;
    };
    AggregationSettings.prototype.getInternalGroups = function () {
        return this.groups;
    };
    AggregationSettings.prototype.lastGroup = function () {
        var groups = this.getGroups();
        return groups[groups.length - 1];
    };
    AggregationSettings.prototype.getAggregates = function () {
        return this.aggregates;
    };
    AggregationSettings.prototype.hasAggregates = function () {
        return this.aggregates.length > 0;
    };
    AggregationSettings.prototype.hasGroups = function () {
        return this.groups.length > 0;
    };
    AggregationSettings.prototype.hasGrandTotals = function () {
        return this.useGrandTotals;
    };
    AggregationSettings.prototype.hasRecordCount = function () {
        return this.useRecordCount;
    };
    AggregationSettings.prototype.isEmpty = function () {
        return !(this.hasAggregates() || this.hasGroups() ||
            this.hasAggregates() || this.hasRecordCount());
    };
    AggregationSettings.prototype.drop = function () {
        console.warn('"drop()" method is obsolete. Use "clear()" instead');
        this.clear();
    };
    AggregationSettings.prototype.clear = function () {
        this.groups = [];
        this.aggregates = [];
        this.useGrandTotals = false;
        this.useRecordCount = false;
        this.caseSensitiveGroups = false;
        return this;
    };
    /**
     * Checks if all columns from the list passed in the parameter are "unused".
     * Here "unused column" means a column that is included neither in any group nor in the aggregates list.
     * @param cols - the array of column IDs
     * @returns true if all columns in the list are not used anywhere, othervise - fals
     */
    AggregationSettings.prototype.hasColumnsInUse = function (cols) {
        for (var _i = 0, _a = this.groups; _i < _a.length; _i++) {
            var group = _a[_i];
            var interCols = group.columns
                .filter(function (c) { return cols.indexOf(c) >= 0; });
            if (interCols.length > 0)
                return true;
        }
        for (var _b = 0, _c = this.aggregates; _b < _c.length; _b++) {
            var aggr = _c[_b];
            if (cols.indexOf(aggr.colId) >= 0)
                return true;
        }
        return false;
    };
    AggregationSettings.prototype.needAggrCalculation = function () {
        return (this.hasAggregates() || this.hasRecordCount())
            && (this.hasGrandTotals() || this.hasGroups());
    };
    AggregationSettings.prototype.saveToData = function () {
        return {
            groups: Array.from(this.groups),
            ugt: this.useGrandTotals,
            urc: this.useRecordCount,
            csg: this.caseSensitiveGroups,
            aggregates: Array.from(this.aggregates)
        };
    };
    AggregationSettings.prototype.loadFromData = function (data) {
        if (data) {
            if (typeof data.ugt !== 'undefined')
                this.useGrandTotals = data.ugt;
            if (typeof data.urc !== 'undefined')
                this.useRecordCount = data.urc;
            if (typeof data.csg !== 'undefined')
                this.caseSensitiveGroups = data.csg;
            if (data.groups) {
                this.groups = Array.from(data.groups);
            }
            if (data.aggregates) {
                this.aggregates = Array.from(data.aggregates);
            }
        }
    };
    AggregationSettings.prototype.buildGroupKey = function (group, row) {
        var caseInsensitive = !this.caseSensitiveGroups;
        var result = {};
        if (group) {
            for (var _i = 0, _a = group.columns; _i < _a.length; _i++) {
                var colId = _a[_i];
                var keyVal = row.getValue(colId);
                if (caseInsensitive && typeof (keyVal) === 'string') {
                    keyVal = keyVal.toLowerCase();
                }
                result[colId] = keyVal;
            }
        }
        return result;
    };
    //returns true if value1 == value2
    AggregationSettings.prototype.strictCompare = function (value1, value2) {
        if (value1 instanceof Date) {
            return value1.getTime() === value2.getTime();
        }
        else
            return value1 === value2;
    };
    //makes a case insensative comparision of two values and return true if there are equal
    AggregationSettings.prototype.caseInsensitiveCompare = function (value1, value2) {
        if (value1 instanceof Date) {
            return value1.getTime() === value2.getTime();
        }
        else {
            var val1 = (typeof value1 === 'string') ? value1.toLowerCase() : value1;
            var val2 = (typeof value2 === 'string') ? value2.toLowerCase() : value2;
            return val1 === val2;
        }
    };
    return AggregationSettings;
}());
export { AggregationSettings };
//# sourceMappingURL=aggr_settings.js.map